import React, { useContext, useState, useEffect, createContext, useRef } from 'react';
import CreateRefinedProjectsList from '/src/services/CreateRefinedProjectsList';
import DIYGuidance from './DIYGuidance/DIYGuidance';
import Discounts from './Discounts/Discounts';
import About from './About/About';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import CreateAPlan from './CreateAPlan/CreateAPlan';
import MyPlans from './MyPlans/MyPlans';
import BeforeYouStart from './BeforeYouStart/BeforeYouStart';
import PlanSummary from './PlanSummary/PlanSummary';
import EngagingPros from './EngagingPros/EngagingPros';
import CompeteOverMe from './CompeteOverMe/CompeteOverMe';
import DownArrowIconLight from '/src/assets/down-arrow-icon-light.svg';
import UpArrowIconLight from '/src/assets/up-arrow-icon-light.svg';

import './style.css';


// Import the Contexts
import { AreaDataContext } from '../PropertyExplorer';
import { AddressSearchContext, WindowSizeContext } from '/src/App';


// Create the Context
export const TakingActionContext = createContext();



const TakingAction = ({ propertyData }) => {
    const { deviceType } = useContext(WindowSizeContext);

    const aboutSlug = 'about';
    const myPlansSlug = 'my-plans';
    const planSummarySlug = 'plan-summary';
    const financingOptionsSlug = 'financing-options';
    const discountsSlug = 'discounts';
    const diyGuidanceSlug = 'diy-guidance';
    const engagingProsSlug = 'engaging-pros';
    const competeOverMeSlug = 'compete-over-me';

    const TAKING_ACTION_VIEWS = [
        {
            id: planSummarySlug,
            label: 'Plan Summary',
            description: "Know who will reward you for your resilience!",
            icon_green: 'plan-summary-green.png',
            icon_tan: 'plan-summary-tan.png',
            icon_grey: 'plan-summary-grey.png',
        },
        {
            id: financingOptionsSlug,
            label: 'Financing Options',
            description: "Know who will reward you for your resilience!",
            icon_green: 'before-you-start-green.png',
            icon_tan: 'before-you-start-tan.png',
            icon_grey: 'before-you-start-grey.png',
        },
        {
            id: discountsSlug,
            label: 'Obtaining Discounts',
            description: "Know who will reward you for your resilience!",
            icon_green: 'taking-action-stage-discounts-available-icon-green.png',
            icon_tan: 'taking-action-stage-discounts-available-icon-tan.png',
            icon_grey: 'taking-action-stage-discounts-available-icon-grey.png',
        },
        {
            id: diyGuidanceSlug,
            label: 'DIY Guidance',
            description: "Looking to get your hands dirty? We've got you covered!",
            icon_green: 'planning-stage-diy-resources-icon-green.png',
            icon_tan: 'planning-stage-diy-resources-icon-tan.png',
            icon_grey: 'planning-stage-diy-resources-icon-grey.png',
        },
        {
            id: engagingProsSlug,
            label: 'Engaging Pros',
            description: "Need help choosing the right vendor? We can help!",
            icon_green: 'planning-stage-engage-vendors-icon-green.png',
            icon_tan: 'planning-stage-engage-vendors-icon-tan.png',
            icon_grey: 'planning-stage-engage-vendors-icon-grey.png',
        },
        {
            id: competeOverMeSlug,
            label: 'Have Companies Compete Over You',
            beta: true,
            description: "Better yet, let's have companies compete for your business!",
            icon_green: 'taking-action-stage-let-companies-compete-over-me-icon-green.png',
            icon_tan: 'taking-action-stage-let-companies-compete-over-me-icon-tan.png',
            icon_grey: 'taking-action-stage-let-companies-compete-over-me-icon-grey.png',
        }
    ];

    // Take the TAKING_ACTION_VIEWS and create copy from index 1 to the end
    const TAKING_ACTION_VIEWS_WITHOUT_MY_PROJECTS = TAKING_ACTION_VIEWS.slice(1);

    // Get the portalData from the AddressSearchContext
    const { portalData, user, handleExplainerViewed } = useContext(AddressSearchContext);

    // Load the discounts from local storage
    const discounts_local = portalData.discounts || [];

    // Load the companies from local storage
    const companies_local = portalData.companies || [];

    // Get the user plans from the AreaDataContext
    const { userPlans = [], setUserPlans, handleUrlSubsectionChange } = useContext(AreaDataContext);

    // Determine if first view should be about or jump right to my-plans, based on user.projectsExplainerViewed
    const firstView = user.projectsExplainerViewed ? myPlansSlug : aboutSlug;

    const {
        tapProjects,
    } = useContext(AreaDataContext);

    const [currentView, setCurrentView] = useState(firstView);
    const [selectedSectionMenuOptions, setSelectedSectionMenuOptions] = useState(TAKING_ACTION_VIEWS);

    const [refinedProjectsList, setRefinedProjectsList] = useState([]);
    const [selectedProjectsList, setSelectedProjectsList] = useState([]);
    const [notSelectedProjectsList, setNotSelectedProjectsList] = useState([]);
    const [userPlanSelectionId, setUserPlanSelectionId] = useState(null);
    const [userPlanSelection, setUserPlanSelection] = useState(null);
    const [isExecutionMultiPlanView, setIsExecutionMultiPlanView] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const containerRef = useRef(null);

    // useEffect to handle the url subsection change
    useEffect(() => {
        if (currentView) {
            handleUrlSubsectionChange(currentView);
        }
    }, [currentView]);


    useEffect(() => {
        if (propertyData) {
            // Deal with the projects list
            const projects_list = propertyData?.data?.recommended_projects?.projects_list || [];
            const refinedProjectsList = CreateRefinedProjectsList(projects_list, propertyData);
            setRefinedProjectsList(refinedProjectsList);

            // Reload the user from local storage and find the plans for this property
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            // Use filter instead of find and provide fallback empty array
            const propertyPlans = user.userPlans
                ? user.userPlans.filter(item => item.property_id === propertyData.address_id)
                : [];

            // Get all plans from matching properties
            const userPlansLocal = propertyPlans.length > 0
                ? propertyPlans.flatMap(property => property.plans || [])
                : [];

            if (userPlansLocal.length > 0) {
                setUserPlans(userPlansLocal);
            }
        }
    }, [propertyData]);

    useEffect(() => {
        if (refinedProjectsList) {
            const selectedProjectsList = refinedProjectsList.filter(project => tapProjects.has(project.project_id));
            const notSelectedProjectsList = refinedProjectsList.filter(project => !tapProjects.has(project.project_id));
            setSelectedProjectsList(selectedProjectsList);
            setNotSelectedProjectsList(notSelectedProjectsList);
        }
    }, [refinedProjectsList, tapProjects]);

    // Handler to confirm about page is complete and user is ready to begin (switch active view)
    const handleAboutComplete = () => {
        setCurrentView(myPlansSlug);
        handleExplainerViewed('takingActionExplainerViewed');
    };

    const handlePlanClick = (planId) => {
        setUserPlanSelectionId(planId);
        setCurrentView(planSummarySlug);
        // handleSectionToggle('lets-get-it-done');
    }

    const handleCreatePlanComplete = () => {
        setCurrentView(myPlansSlug);
    };

    const handleCreateFirstPlan = () => {
        // handleSectionToggle('lets-make-a-plan');
        setCurrentView('create-a-plan');
    }

    const goToCompeteOverMe = () => {
        setCurrentView(competeOverMeSlug);
    }

    const handlePlanUpdate = (updatedPlan) => {

        // console.log('updatedPlan WE NEED TO SAVE', updatedPlan);
        // Get fresh user data from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // console.log('user.userPlans', user.userPlans);

        // Find the property object
        const propertyIndex = user.userPlans.findIndex(item => item.property_id === propertyData.address_id);
        // console.log('propertyIndex', propertyIndex);
        if (propertyIndex !== -1) {
            // Find the plan in the property's plans based on the planId
            const planIndex = user.userPlans[propertyIndex].plans.findIndex(plan => plan.planId === updatedPlan.planId);

            // Update the property's plans
            user.userPlans[propertyIndex].plans[planIndex] = updatedPlan;

            // Get the updated userPlans
            const updatedUserPlans = user.userPlans[propertyIndex].plans;

            // Update local state and localStorage
            // console.log('NEW user.userPlans', user.userPlans);
            setUserPlans(updatedUserPlans);
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    const handlePlanDelete = (planId) => {
        // Get fresh user data from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        // Find the property object
        const propertyIndex = user.userPlans.findIndex(item => item.property_id === propertyData.address_id);

        if (propertyIndex !== -1) {
            // Filter out the deleted plan
            const updatedPropertyPlans = user.userPlans[propertyIndex].plans.filter(plan => plan.planId !== planId);

            // Update the property's plans
            user.userPlans[propertyIndex].plans = updatedPropertyPlans;

            // If no plans left for this property, remove the property object
            if (updatedPropertyPlans.length === 0) {
                user.userPlans.splice(propertyIndex, 1);
            }

            // Update local state and localStorage
            setUserPlans(updatedPropertyPlans);
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    // console.log('currentView', currentView);

    const handleNavButtonClick = (view) => {
        // console.log('handleNavButtonClick', view);
        // console.log('TAKING_ACTION_VIEWS', TAKING_ACTION_VIEWS);
        // console.log('selectedSectionMenuOptions', selectedSectionMenuOptions);
        // console.log('userPlans.length', userPlans.length);
        if (TAKING_ACTION_VIEWS === selectedSectionMenuOptions || userPlans.length === 0) {
            handleCreateFirstPlan();
        } else {
            setCurrentView(view);
        }

        // Scroll to top of the page
        window.scrollTo(0, 0);
    }

    useEffect(() => {
        // console.log('userPlans.length', userPlans.length);
        // console.log('currentView', currentView);
        // console.log('myPlansSlug', myPlansSlug);
        // console.log('isExecutionMultiPlanView', isExecutionMultiPlanView);

        if (userPlans.length > 1 && currentView !== myPlansSlug) {
            // If set to false, set to true
            if (!isExecutionMultiPlanView) {
                setIsExecutionMultiPlanView(true);
            }
        }
        else {
            if (isExecutionMultiPlanView) {
                setIsExecutionMultiPlanView(false);
            }
        }
    }, [userPlans, currentView]);

    const handleUserPlanDropdownSelection = (planId) => {
        setUserPlanSelectionId(planId);
    }

    useEffect(() => {
        // If the userPlanSelection is not null and the planId is in the userPlans array
        if (userPlanSelectionId && userPlans.some(plan => plan.planId === userPlanSelectionId)) {

            setUserPlanSelection(userPlans.find(plan => plan.planId === userPlanSelectionId));

            // If the userPlanSelection is null and the userPlans array is not empty
        } else if (!userPlanSelectionId && userPlans.length > 0) {

            // Get the most recent plan based on the epochTimestamp key
            const mostRecentPlan = userPlans.sort((a, b) => b.epochTimestamp - a.epochTimestamp)[0];
            setUserPlanSelection(mostRecentPlan);
        }
    }, [userPlanSelectionId, userPlans]);

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };
    console.log('isExecutionMultiPlanView', isExecutionMultiPlanView);

    return (
        <TakingActionContext.Provider value={{ currentView, setCurrentView, selectedProjectsList, notSelectedProjectsList }}>

            <div className={`risk-overview-individual-section-container ${deviceType}`}>
                {currentView !== 'about' && (
                    <>
                        <button
                            key={'taking-action-my-plans-button'}
                            className={`taking-action-nav-button my-plans ${deviceType}`}
                            onClick={() => handleNavButtonClick(myPlansSlug)}
                        >
                            <div className={`taking-action-nav-button-icon-wrapper expanded`}>
                                <img src={CreateCloudfrontUrl('my-projects-list-green.png')} alt="My Plans" className="taking-action-nav-button-icon" />
                            </div>
                            <div className={`taking-action-nav-button-text-wrapper expanded`}>
                                <span className='text-align-center'>My Plans</span>
                            </div>
                        </button>
                        <div className={`taking-action-layout expanded ${deviceType}`}>
                            {deviceType === 'mobile' ? (
                                <div
                                    ref={containerRef}
                                    className={`taking-action-mobile-nav ${menuOpen ? 'open' : ''}`}
                                >
                                    {isExecutionMultiPlanView && (
                                        <div className="taking-action-nav-button-wrapper">
                                            <div className="taking-action-plan-dropdown">
                                                <select
                                                    className="taking-action-plan-select"
                                                    value={userPlanSelectionId || ''}
                                                    onChange={(e) => handleUserPlanDropdownSelection(e.target.value)}
                                                >
                                                    {/* Show current selected plan first */}
                                                    {userPlanSelectionId && (
                                                        <option value={userPlanSelectionId}>
                                                            {userPlans.find(plan => plan.planId === userPlanSelectionId)?.planName}
                                                        </option>
                                                    )}
                                                    {/* Show other plans */}
                                                    {userPlans
                                                        .filter(plan => plan.planId !== userPlanSelectionId)
                                                        .map(plan => (
                                                            <option
                                                                key={plan.planId}
                                                                value={plan.planId}
                                                            >
                                                                {plan.planName}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        className="taking-action-mobile-toggle taking-action-nav-button taking-action-nav-button-active"
                                        onClick={toggleMenu}
                                    >
                                        <div className="taking-action-nav-button-icon-wrapper expanded">
                                            <img
                                                src={CreateCloudfrontUrl(
                                                    selectedSectionMenuOptions.find(view => view.id === currentView)?.icon_tan ||
                                                    selectedSectionMenuOptions[0].icon_tan
                                                )}
                                                alt="Current Section"
                                                className="taking-action-nav-button-icon"
                                            />
                                        </div>
                                        <div className="taking-action-nav-button-text-wrapper expanded">
                                            <span className='text-align-center'>
                                                {selectedSectionMenuOptions.find(view => view.id === currentView)?.label ||
                                                    selectedSectionMenuOptions[0].label}
                                            </span>
                                        </div>
                                        <img
                                            src={menuOpen ? UpArrowIconLight : DownArrowIconLight}
                                            alt="Toggle Navigation"
                                            className="taking-action-mobile-arrow"
                                        />
                                    </button>
                                    {menuOpen && (
                                        <div className="taking-action-mobile-menu">
                                            {selectedSectionMenuOptions.map(view => {
                                                const icon = currentView === view.id ? view.icon_tan : view.icon_grey;
                                                const isBeta = view.beta || false;
                                                return (
                                                    <button
                                                        key={view.id}
                                                        className={`taking-action-nav-button ${currentView === view.id ? 'taking-action-nav-button-active' : 'taking-action-nav-button-inactive'}`}
                                                        onClick={() => {
                                                            handleNavButtonClick(view.id);
                                                            setMenuOpen(false);
                                                        }}
                                                        style={{ borderRadius: 0 }}
                                                    >
                                                        <div className="taking-action-nav-button-icon-wrapper expanded">
                                                            <img src={CreateCloudfrontUrl(icon)} alt={view.label} className="taking-action-nav-button-icon" />
                                                        </div>
                                                        <div className="taking-action-nav-button-text-wrapper expanded">
                                                            <span className='text-align-center'>{view.label}</span>
                                                            {isBeta && (
                                                                <div className={`taking-action-nav-button-beta-container ${currentView === view.id ? 'taking-action-nav-button-beta-container-active' : ''}`}>
                                                                    <span className='taking-action-nav-button-beta-text'>Beta</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={`taking-action-nav expanded`}>
                                    {isExecutionMultiPlanView && (
                                        <div className="taking-action-nav-button-wrapper">
                                            <div className="taking-action-plan-dropdown">
                                                <select
                                                    className="taking-action-plan-select"
                                                    value={userPlanSelectionId || ''}
                                                    onChange={(e) => handleUserPlanDropdownSelection(e.target.value)}
                                                >
                                                    {/* Show current selected plan first */}
                                                    {userPlanSelectionId && (
                                                        <option value={userPlanSelectionId}>
                                                            {userPlans.find(plan => plan.planId === userPlanSelectionId)?.planName}
                                                        </option>
                                                    )}
                                                    {/* Show other plans */}
                                                    {userPlans
                                                        .filter(plan => plan.planId !== userPlanSelectionId)
                                                        .map(plan => (
                                                            <option
                                                                key={plan.planId}
                                                                value={plan.planId}
                                                            >
                                                                {plan.planName}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                    {selectedSectionMenuOptions.map(view => {
                                        const icon = currentView === view.id ? view.icon_tan : view.icon_grey;
                                        const isBeta = view.beta || false;
                                        return (
                                            <button
                                                key={view.id}
                                                className={`taking-action-nav-button ${currentView === view.id ? 'taking-action-nav-button-active' : 'taking-action-nav-button-inactive'}`}
                                                onClick={() => handleNavButtonClick(view.id)}
                                            >
                                                <div className={`taking-action-nav-button-icon-wrapper expanded`}>
                                                    <img src={CreateCloudfrontUrl(icon)} alt={view.label} className="taking-action-nav-button-icon" />
                                                </div>
                                                <div className={`taking-action-nav-button-text-wrapper expanded`}>
                                                    <span className='text-align-center'>{view.label}</span>
                                                    {isBeta && (
                                                        <div className={`taking-action-nav-button-beta-container ${currentView === view.id ? 'taking-action-nav-button-beta-container-active' : ''}`}>
                                                            <span className='taking-action-nav-button-beta-text'>Beta</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <div className={`taking-action-content ${deviceType}`}>

                                {currentView === 'create-a-plan' && <CreateAPlan propertyData={propertyData} projects={refinedProjectsList} userPlans={userPlans} setUserPlans={setUserPlans} handleCreatePlanComplete={handleCreatePlanComplete} />}
                                {currentView === myPlansSlug && <MyPlans handlePlanClick={handlePlanClick} handlePlanDelete={handlePlanDelete} handlePlanUpdate={handlePlanUpdate} handleCreateFirstPlan={handleCreateFirstPlan} userPlans={userPlans} projects={refinedProjectsList} propertyData={propertyData} />}
                                {currentView === planSummarySlug && <PlanSummary selectedPlan={userPlanSelection} propertyData={propertyData} handleCreateFirstPlan={handleCreateFirstPlan} />}
                                {currentView === financingOptionsSlug && <BeforeYouStart />}
                                {currentView === discountsSlug && <Discounts propertyData={propertyData} userPlanSelection={userPlanSelection} discounts={discounts_local} companies={companies_local} projects={refinedProjectsList} handleGoToCompeteOverMe={goToCompeteOverMe} />}
                                {currentView === diyGuidanceSlug && <DIYGuidance propertyData={propertyData} userPlanSelection={userPlanSelection} companies={companies_local} projects={refinedProjectsList} />}
                                {currentView === engagingProsSlug && <EngagingPros propertyData={propertyData} userPlanSelection={userPlanSelection} companies={companies_local} projects={refinedProjectsList} />}
                                {currentView === competeOverMeSlug && <CompeteOverMe propertyData={propertyData} userPlanSelection={userPlanSelection} companies={companies_local} projects={refinedProjectsList} />}
                            </div>
                        </div>
                    </>
                )}
                {currentView === aboutSlug && (
                    <About handleAboutComplete={handleAboutComplete} propertyData={propertyData} subsections={TAKING_ACTION_VIEWS_WITHOUT_MY_PROJECTS} />
                )}
            </div>
        </TakingActionContext.Provider>
    );
};

export default TakingAction;
